/**
 * @file ImageComponent.tsx
 * @description
 * -----------------------------------------------------
 * Componente de Image
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-02-25
 * @modified 2025-09-24
 * @author DLTCode
 * @author Juan González
 * @author Ismael Benabdellah
 * @author Nacho Moya
 * @copyright © DLTCode
 * @license Private
 * */

"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import ButtonComponent from "@/components/ui/ButtonComponent";
import { ImageProps } from "@/types/ui";
import { XIcon } from "lucide-react";
import { FocusTrap } from "focus-trap-react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
const ImageComponent = t0 => {
  const $ = _c(59);
  let alt;
  let onClick;
  let onError;
  let parentButtonStyle;
  let props;
  let src;
  let t1;
  let t2;
  let t3;
  let t4;
  let title;
  if ($[0] !== t0) {
    ({
      src,
      alt,
      title,
      width: t1,
      height: t2,
      className: t3,
      zoomable: t4,
      onError,
      onClick,
      parentButtonStyle,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = alt;
    $[2] = onClick;
    $[3] = onError;
    $[4] = parentButtonStyle;
    $[5] = props;
    $[6] = src;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = title;
  } else {
    alt = $[1];
    onClick = $[2];
    onError = $[3];
    parentButtonStyle = $[4];
    props = $[5];
    src = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    title = $[11];
  }
  const width = t1 === undefined ? 40 : t1;
  const height = t2 === undefined ? 40 : t2;
  const className = t3 === undefined ? "" : t3;
  const zoomable = t4 === undefined ? true : t4;
  const button = useTranslations("Buttons");
  const t = useTranslations("ImageComponent");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [previewSize, setPreviewSize] = useState(null);
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e => {
      const img = e.currentTarget;
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;
      const maxWidth = window.innerWidth * 0.75;
      const maxHeight = window.innerHeight * 0.75;
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const scale = Math.min(widthRatio, heightRatio);
      setPreviewSize({
        width: naturalWidth * scale,
        height: naturalHeight * scale
      });
    };
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleImageLoad = t5;
  let t6;
  if ($[13] !== zoomable) {
    t6 = () => {
      if (zoomable) {
        setIsPreviewOpen(true);
      }
    };
    $[13] = zoomable;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleOpenPreview = t6;
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setIsPreviewOpen(false);
    };
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleClosePreview = t7;
  let t8;
  if ($[16] !== onError) {
    t8 = () => {
      setHasError(true);
      if (onError) {
        onError();
      }
    };
    $[16] = onError;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleImageError = t8;
  let t9;
  bb0: {
    if (!isPreviewOpen) {
      t9 = null;
      break bb0;
    }
    let t10;
    let t11;
    let t12;
    let t13;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = {
        opacity: 0
      };
      t11 = {
        opacity: 1
      };
      t12 = {
        duration: 0.2
      };
      t13 = {
        opacity: 0
      };
      $[18] = t10;
      $[19] = t11;
      $[20] = t12;
      $[21] = t13;
    } else {
      t10 = $[18];
      t11 = $[19];
      t12 = $[20];
      t13 = $[21];
    }
    let t14;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = e_0 => {
        if (e_0.key === "Escape") {
          handleClosePreview();
        }
      };
      $[22] = t14;
    } else {
      t14 = $[22];
    }
    let t15;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = e_1 => {
        e_1.stopPropagation();
        handleClosePreview();
      };
      $[23] = t15;
    } else {
      t15 = $[23];
    }
    let t16;
    if ($[24] !== button) {
      t16 = button("buttonClosePreview");
      $[24] = button;
      $[25] = t16;
    } else {
      t16 = $[25];
    }
    let t17;
    if ($[26] !== button) {
      t17 = button("buttonClosePreview");
      $[26] = button;
      $[27] = t17;
    } else {
      t17 = $[27];
    }
    let t18;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <XIcon role="img" aria-hidden={true} style={{
        height: 30,
        width: 30
      }} />;
      $[28] = t18;
    } else {
      t18 = $[28];
    }
    let t19;
    if ($[29] !== t16 || $[30] !== t17) {
      t19 = <ButtonComponent onClick={t15} className="image__preview__close" title={t16} aria-label={t17}>{t18}</ButtonComponent>;
      $[29] = t16;
      $[30] = t17;
      $[31] = t19;
    } else {
      t19 = $[31];
    }
    let t20;
    if ($[32] !== previewSize) {
      t20 = previewSize ? {
        width: previewSize.width,
        height: previewSize.height
      } : undefined;
      $[32] = previewSize;
      $[33] = t20;
    } else {
      t20 = $[33];
    }
    let t21;
    if ($[34] !== alt || $[35] !== src || $[36] !== t20) {
      t21 = <img src={src} alt={alt} className="image__preview__content" onClick={_temp} onLoad={handleImageLoad} style={t20} />;
      $[34] = alt;
      $[35] = src;
      $[36] = t20;
      $[37] = t21;
    } else {
      t21 = $[37];
    }
    let t22;
    if ($[38] !== t19 || $[39] !== t21) {
      t22 = createPortal(<FocusTrap active={true}><motion.dialog initial={t10} animate={t11} transition={t12} exit={t13} className="image__preview" onClick={handleClosePreview} onKeyDown={t14} aria-modal="true" role="dialog">{t19}{t21}</motion.dialog></FocusTrap>, document.body);
      $[38] = t19;
      $[39] = t21;
      $[40] = t22;
    } else {
      t22 = $[40];
    }
    t9 = t22;
  }
  const avatarDialog = t9;
  let t10;
  if ($[41] !== alt || $[42] !== className || $[43] !== handleImageError || $[44] !== handleOpenPreview || $[45] !== hasError || $[46] !== height || $[47] !== onClick || $[48] !== parentButtonStyle || $[49] !== props || $[50] !== src || $[51] !== t || $[52] !== title || $[53] !== width || $[54] !== zoomable) {
    t10 = !hasError ? zoomable || onClick ? <button type="button" onClick={() => {
      if (onClick) {
        onClick();
      } else {
        handleOpenPreview();
      }
    }} tabIndex={0} style={parentButtonStyle}><Image src={src} alt={alt} title={title} width={width} height={height} className={className === "sidebar__img" ? className : `image ${zoomable ? "image--zoomable" : ""} ${className}`} onError={handleImageError} {...props} /></button> : <Image src={src} alt={alt} title={title} width={width} height={height} className={className === "sidebar__img" ? className : `image ${className}`} onError={handleImageError} {...props} /> : <figure className="image-fallback" aria-hidden="true"><span>{t("loadError")}</span></figure>;
    $[41] = alt;
    $[42] = className;
    $[43] = handleImageError;
    $[44] = handleOpenPreview;
    $[45] = hasError;
    $[46] = height;
    $[47] = onClick;
    $[48] = parentButtonStyle;
    $[49] = props;
    $[50] = src;
    $[51] = t;
    $[52] = title;
    $[53] = width;
    $[54] = zoomable;
    $[55] = t10;
  } else {
    t10 = $[55];
  }
  let t11;
  if ($[56] !== avatarDialog || $[57] !== t10) {
    t11 = <>{t10}{avatarDialog}</>;
    $[56] = avatarDialog;
    $[57] = t10;
    $[58] = t11;
  } else {
    t11 = $[58];
  }
  return t11;
};
export default ImageComponent;
function _temp(e_2) {
  return e_2.stopPropagation();
}